'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { cx } from '../utils/cx';
import styles from './Avatar.module.css';
const AVATAR_TONES = [
    ['var(--avatar-tone-1-bg)', 'var(--avatar-tone-1-fg)'],
    ['var(--avatar-tone-2-bg)', 'var(--avatar-tone-2-fg)'],
    ['var(--avatar-tone-3-bg)', 'var(--avatar-tone-3-fg)'],
    ['var(--avatar-tone-4-bg)', 'var(--avatar-tone-4-fg)'],
    ['var(--avatar-tone-5-bg)', 'var(--avatar-tone-5-fg)'],
    ['var(--avatar-tone-6-bg)', 'var(--avatar-tone-6-fg)'],
    ['var(--avatar-tone-7-bg)', 'var(--avatar-tone-7-fg)'],
    ['var(--avatar-tone-8-bg)', 'var(--avatar-tone-8-fg)'],
];
// 32-bit FNV-1a. Distributes single-letter prefixes (Aisha vs. Aaron) across
// the tone palette far better than charCodeAt(0).
const fnv1a = (key) => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < key.length; i++) {
        hash ^= key.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
};
const toneFor = (key) => {
    if (!key)
        return AVATAR_TONES[0];
    return AVATAR_TONES[fnv1a(key) % AVATAR_TONES.length] ?? AVATAR_TONES[0];
};
export const Avatar = forwardRef(function Avatar({ src, alt, name, size = 'md', ring, decorative = false, className, href, linkAs }, ref) {
    const [errored, setErrored] = useState(false);
    // Reset error state when src changes so a new url gets a fresh load attempt.
    useEffect(() => {
        setErrored(false);
    }, [src]);
    const a11yName = alt ?? name ?? '';
    const showImage = src && !errored;
    const initials = name ? computeInitials(name) : '';
    const isNumericSize = typeof size === 'number';
    const [bg, fg] = toneFor(name ?? a11yName);
    const classes = cx(styles.root, !isNumericSize && styles[`size-${size}`], ring && styles.ring, className);
    const inlineStyle = {
        background: bg,
        color: fg,
        ...(isNumericSize
            ? {
                width: `${size}px`,
                height: `${size}px`,
                ['--avatar-circle-size']: `${size}px`,
            }
            : {}),
    };
    // When an image is rendered, the <img alt> carries the accessible name —
    // adding role="img" + aria-label to the wrapper would announce twice.
    // Fallbacks (initials/icon) are decorative themselves, so the wrapper takes
    // the role + label. In `decorative` mode, the avatar carries no accessible
    // name at any layer — a visible name in the same row carries it instead.
    const wrapperA11y = decorative
        ? { 'aria-hidden': true }
        : showImage
            ? {}
            : { role: 'img', 'aria-label': a11yName };
    const imageAlt = decorative ? '' : a11yName;
    const inner = (_jsx("span", { ref: ref, className: classes, style: inlineStyle, ...wrapperA11y, children: showImage ? (_jsx("img", { src: src, alt: imageAlt, className: styles.image, loading: "lazy", decoding: "async", onError: () => setErrored(true), draggable: false })) : initials ? (_jsx("span", { "aria-hidden": "true", className: styles.initials, children: initials })) : (_jsx(Icon, { name: "account", className: styles.icon })) }));
    if (href) {
        const LinkAs = (linkAs ?? 'a');
        return (_jsx(LinkAs, { href: href, style: { display: 'inline-flex', color: 'inherit', textDecoration: 'none' }, "aria-label": decorative ? undefined : a11yName, children: inner }));
    }
    return inner;
});
function computeInitials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return '';
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
}
//# sourceMappingURL=Avatar.js.map